/**
 * 🏢 Coach Enterprise Offers Section
 * ==================================
 * Affiche dans le CoachDashboard les offres entreprise (Appel d'offre coach)
 * proposées par l'admin. Le coach peut Accepter / Refuser chaque offre pending.
 *
 * Vague 4 P0 — Tarif coach (2b) : on affiche uniquement le tarif net coach +
 * gains totaux projetés. Le tarif client n'est jamais exposé.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Building2, Check, X, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:  { label: 'En attente', cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  accepted: { label: 'Acceptée',   cls: 'bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]' },
  declined: { label: 'Refusée',    cls: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]' },
  expired:  { label: 'Expirée',    cls: 'bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]' },
  revoked:  { label: 'Retirée',    cls: 'bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]' },
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const daysUntil = (iso) => {
  try {
    const d = new Date(iso).getTime();
    const diff = Math.ceil((d - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch { return null; }
};

const typeLabel = (t) => ((t || '').toLowerCase() === 'ia' ? 'Accompagnement IA' : 'Coaching humain');

export default function CoachEnterpriseOffers({ token }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null); // { offer, action }
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/coach/enterprise-offers`, { headers });
      if (!res.ok) throw new Error('Échec chargement des offres');
      const d = await res.json();
      setOffers(d.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const openConfirm = (offer, action) => {
    setNote('');
    setConfirmModal({ offer, action });
  };

  const respond = async () => {
    if (!confirmModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/coach/enterprise-offers/${confirmModal.offer.id}/respond`,
        {
          method: 'POST', headers,
          body: JSON.stringify({ action: confirmModal.action, note: note || null }),
        },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(typeof e.detail === 'string' ? e.detail : 'Erreur');
      }
      toast.success(confirmModal.action === 'accept' ? 'Offre acceptée' : 'Offre refusée');
      setConfirmModal(null);
      fetchOffers();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const pending = offers.filter(o => o.status === 'pending');
  const history = offers.filter(o => o.status !== 'pending');

  return (
    <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5 sm:p-6" data-testid="coach-enterprise-offers">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#92710A] to-[#B8860B] flex items-center justify-center">
            <Building2 className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <h2 className="text-[#111827] font-display text-lg leading-tight">Mes offres entreprise</h2>
            <p className="text-[12px] text-[#4B5563] mt-0.5">Missions proposées par l’équipe Coach DIA</p>
          </div>
        </div>
        {pending.length > 0 && (
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] font-semibold"
            data-testid="coach-offers-pending-count">
            {pending.length} à traiter
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 flex items-center justify-center text-[#6B7280] text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : offers.length === 0 ? (
        <div className="py-8 text-center text-[13px] text-[#6B7280]" data-testid="coach-offers-empty">
          Aucune offre entreprise pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map(o => <OfferCard key={o.id} offer={o} onAct={openConfirm} />)}
          {history.length > 0 && (
            <details className="mt-4" data-testid="coach-offers-history-toggle">
              <summary className="text-[12px] text-[#4B5563] cursor-pointer hover:text-[#111827] font-medium">
                Historique ({history.length})
              </summary>
              <div className="mt-3 space-y-3">
                {history.map(o => <OfferCard key={o.id} offer={o} onAct={openConfirm} readonly />)}
              </div>
            </details>
          )}
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !submitting && setConfirmModal(null)}
          data-testid="coach-offer-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              {confirmModal.action === 'accept' ? (
                <div className="w-10 h-10 rounded-full bg-[#D1FAE5] flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#047857]" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                  <X className="w-5 h-5 text-[#991B1B]" />
                </div>
              )}
              <h3 className="font-display text-lg text-[#111827]">
                {confirmModal.action === 'accept' ? 'Accepter l’offre ?' : 'Refuser l’offre ?'}
              </h3>
            </div>
            <p className="text-[13px] text-[#4B5563] mb-3">
              Société : <strong>{(confirmModal.offer.company_snapshot || {}).company_name || '—'}</strong><br/>
              Tarif : <strong>{confirmModal.offer.coach_rate_eur}€/session</strong> · Gains potentiels :
              <strong> {confirmModal.offer.estimated_coach_earnings_max}€</strong>
            </p>
            <label className="text-[11px] text-[#4B5563] block mb-1 uppercase tracking-wider font-semibold">
              Note {confirmModal.action === 'decline' ? '(recommandée)' : '(optionnelle)'}
            </label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={confirmModal.action === 'accept' ? 'Remerciements / disponibilité…' : 'Raison du refus…'}
              data-testid="coach-offer-confirm-note"
              className="w-full p-2 rounded-lg border border-[#E8E5DE] text-[13px] focus:outline-none focus:border-[#92710A]" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setConfirmModal(null)} disabled={submitting}
                data-testid="coach-offer-confirm-cancel"
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#E8E5DE] text-sm text-[#4B5563] hover:bg-[#F8F7F4] disabled:opacity-50">
                Annuler
              </button>
              <button onClick={respond} disabled={submitting}
                data-testid="coach-offer-confirm-submit"
                className={`flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5 ${
                  confirmModal.action === 'accept' ? 'bg-[#047857] hover:bg-[#065F46]' : 'bg-[#991B1B] hover:bg-[#7F1D1D]'
                }`}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (confirmModal.action === 'accept' ? 'Confirmer l’acceptation' : 'Confirmer le refus')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function OfferCard({ offer, onAct, readonly = false }) {
  const meta = STATUS_META[offer.status] || STATUS_META.pending;
  const company = (offer.company_snapshot || {}).company_name || '—';
  const remainingDays = daysUntil(offer.expires_at);
  const urgent = offer.status === 'pending' && remainingDays != null && remainingDays <= 2;

  return (
    <article className={`border rounded-xl p-4 transition-all ${
      offer.status === 'pending' ? 'border-[#E8DFC8] bg-[#FDFBF5]' : 'border-[#E8E5DE] bg-white'
    } ${urgent ? 'ring-2 ring-[#FDE68A]' : ''}`}
      data-testid={`coach-offer-card-${offer.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-[15px] text-[#111827] truncate">{company}</h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${meta.cls}`}
              data-testid={`coach-offer-status-${offer.id}`}>
              {meta.label}
            </span>
          </div>
          <p className="text-[12px] text-[#4B5563] mt-0.5">
            {typeLabel(offer.order_type)} · {offer.order_total_sessions} sessions
          </p>
        </div>
        {urgent && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] inline-flex items-center gap-1 whitespace-nowrap">
            <AlertCircle className="w-3 h-3" /> Urgent · {remainingDays}j
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
        <div className="bg-white border border-[#E8DFC8] rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-[#92710A] font-semibold">Tarif par session</div>
          <div className="text-[#111827] font-display text-lg mt-0.5"
            data-testid={`coach-offer-rate-${offer.id}`}>
            {offer.coach_rate_eur}€
          </div>
        </div>
        <div className="bg-white border border-[#E8DFC8] rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-[#047857] font-semibold">Gains potentiels</div>
          <div className="text-[#047857] font-display text-lg mt-0.5"
            data-testid={`coach-offer-earnings-${offer.id}`}>
            {offer.estimated_coach_earnings_max}€
          </div>
        </div>
      </div>

      {offer.personal_message && (
        <div className="bg-[#FBF9F3] border-l-2 border-[#92710A] rounded-r-md p-2.5 text-[12px] text-[#374151] leading-relaxed mb-3"
          data-testid={`coach-offer-message-${offer.id}`}>
          {offer.personal_message}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-[#6B7280] inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {offer.status === 'pending'
            ? `À répondre avant le ${formatDate(offer.expires_at)}`
            : offer.responded_at ? `Répondu le ${formatDate(offer.responded_at)}` : formatDate(offer.created_at)}
        </div>
        {!readonly && offer.status === 'pending' && (
          <div className="flex gap-2">
            <button onClick={() => onAct(offer, 'decline')}
              data-testid={`coach-offer-decline-${offer.id}`}
              className="px-3 py-1.5 rounded-lg border border-[#FECACA] text-[#991B1B] text-[12px] font-medium hover:bg-[#FEE2E2] inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Refuser
            </button>
            <button onClick={() => onAct(offer, 'accept')}
              data-testid={`coach-offer-accept-${offer.id}`}
              className="px-3 py-1.5 rounded-lg bg-[#047857] text-white text-[12px] font-semibold hover:bg-[#065F46] inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Accepter
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
